import { useCallback } from "react";
import { useFormData } from "cerulean";
import {
  AccountPermissionState,
  RoleFormData,
  reconcileRoleFormData,
} from "./RoleFormUtils";

/**
 * Owns the role form's data for the new and edit role pages. Every change goes through the
 * returned `onChange`, which applies the account and external account rules (see `reconcileRoleFormData`) in the same
 * update, so there are no effects that react to one field by changing another. Pass this
 * `onChange` to `ContextForm` and to anything that updates the form.
 */
export default function useRoleFormData(initialData: RoleFormData) {
  const { formData, onChange: setFields } = useFormData(
    reconcileRoleFormData({}, initialData) as RoleFormData
  );

  const onChange = useCallback(
    (update: Partial<RoleFormData>) =>
      setFields(reconcileRoleFormData(formData, update)),
    [setFields, formData]
  );

  const onAccountPermissionsChange = (
    accountUuid: string,
    next: AccountPermissionState
  ) =>
    onChange({
      accountPermissions: {
        ...formData.accountPermissions,
        [accountUuid]: next,
      },
    });

  return { formData, onChange, onAccountPermissionsChange };
}
